import React, { useState, useEffect, useCallback } from 'react';
import LoginPage from './components/LoginPage';
import TimesheetPage from './components/TimesheetPage';
import AdminPage from './components/AdminPage';
import logo from './CelPlan_New_Logo_2025.png';
import pkg from '../package.json';
const { version } = pkg;
import useLayoutMode from './hooks/useLayoutMode';
import useInactivityLogout from './hooks/useInactivityLogout';
import { LayoutContext } from './LayoutContext';

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('timesheet');
  // 'inactivity' | 'expired' | null — controls the notice shown on the login screen
  const [logoutReason, setLogoutReason] = useState(null);
  const layoutMode = useLayoutMode();
  const { isMobile, isAuto, toggleLayout } = layoutMode;

  // ── Session restore ──────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      if (!parsed.role) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          parsed.role = payload.role || 'user';
          localStorage.setItem('user', JSON.stringify(parsed));
        } catch (e) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return;
        }
      }
      setUser(parsed);
    }
  }, []);

  // ── Central logout ───────────────────────────────────────────────────────
  // reason: 'inactivity' | 'expired' | undefined (manual)
  const handleLogout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setPage('timesheet');
    setLogoutReason(reason || null);
  }, []);

  // ── 5-minute inactivity timer ────────────────────────────────────────────
  useInactivityLogout(handleLogout, !!user);

  // ── session:expired event from api.js (401/403) ──────────────────────────
  useEffect(() => {
    const onExpired = () => handleLogout('expired');
    window.addEventListener('session:expired', onExpired);
    return () => window.removeEventListener('session:expired', onExpired);
  }, [handleLogout]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleLogin = (userData) => {
    setLogoutReason(null);
    setUser(userData);
    setPage('timesheet');
  };

  // ── Logo bar ─────────────────────────────────────────────────────────────
  const logoBar = (
    <div className={`app-logo-bar${isMobile ? ' app-logo-bar--mobile' : ''}`}>
      <div className="app-logo-wrap">
        <img src={logo} alt="CelPlan Logo" className="app-logo" />
        <span className="app-version">v{version}</span>
      </div>
      <button
        className="btn-layout-toggle"
        onClick={toggleLayout}
        title={isMobile ? 'Switch to desktop layout' : 'Switch to mobile layout'}
        aria-label={isMobile ? 'Switch to desktop layout' : 'Switch to mobile layout'}
      >
        {isMobile ? '🖥 Desktop' : '📱 Mobile'}
      </button>
      {!isAuto && (
        <span className="layout-badge">
          {isMobile ? 'Mobile' : 'Desktop'}
        </span>
      )}
    </div>
  );

  return (
    <LayoutContext.Provider value={layoutMode}>
      <div className={isMobile ? 'mobile' : 'desktop'}>
        {logoBar}
        {!user && (
          <LoginPage onLogin={handleLogin} logoutReason={logoutReason} />
        )}
        {user && page === 'admin' && user.role === 'admin' && (
          <AdminPage user={user} onLogout={handleLogout} onBack={() => setPage('timesheet')} />
        )}
        {user && !(page === 'admin' && user.role === 'admin') && (
          <TimesheetPage user={user} onLogout={handleLogout} onAdmin={() => setPage('admin')} />
        )}
      </div>
    </LayoutContext.Provider>
  );
}

export default App;
